import { apiUrl, IS_MOCK } from "./config";

/**
 * Whether the app is running without a configured API and is therefore
 * rendering demo data from `src/mocks`. When false, every service call hits
 * the real backend and FAILURES ARE PROPAGATED so the UI can show an error
 * state instead of silently masking an outage with demo data.
 */
export const IS_OFFLINE = IS_MOCK;

/**
 * Fired when an AUTHENTICATED request is rejected with 401 — i.e. the stored
 * token has expired or is no longer valid (most often after a backend redeploy
 * rotated the signing secret). AuthContext listens for this and clears the
 * session so the user is sent back to the sign-in screen, instead of every
 * console screen silently falling back to demo data.
 */
export const UNAUTHORIZED_EVENT = "myyp:unauthorized";

/** Emits {@link UNAUTHORIZED_EVENT} for a 401 that carried a bearer token. */
function notifyUnauthorized(status: number, token?: string | null): void {
  if (status !== 401 || !token) return;
  try {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  } catch {
    /* non-browser environment — nothing to notify */
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status = 0, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  method?: Method;
  token?: string | null;
  body?: unknown;
  timeoutMs?: number;
  signal?: AbortSignal;
  /** Override the base URL (used by the auth endpoints, which have their own). */
  baseUrl?: string;
}

interface Envelope {
  code?: string;
  data?: unknown;
  message?: string;
  meta?: { message?: string; detail?: string };
  [key: string]: unknown;
}

const DEFAULT_TIMEOUT = 15000;

function resolveUrl(path: string, baseUrl?: string): string {
  return baseUrl ? `${baseUrl.replace(/\/+$/, "")}${path.startsWith("/") ? path : `/${path}`}` : apiUrl(path);
}

function buildHeaders(token?: string | null, hasBody = false): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (hasBody) headers["Content-Type"] = "application/json";
  return headers;
}

function messageFrom(body: Envelope | null, fallback: string): string {
  if (!body) return fallback;
  const candidate = body.meta?.message || body.message || body.meta?.detail;
  return typeof candidate === "string" && candidate.trim() ? candidate : fallback;
}

async function send(
  path: string,
  options: RequestOptions,
): Promise<{ res: Response; body: Envelope | null }> {
  const { method = "GET", token, body, timeoutMs = DEFAULT_TIMEOUT, signal, baseUrl } = options;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  try {
    const res = await fetch(resolveUrl(path, baseUrl), {
      method,
      headers: buildHeaders(token, body !== undefined),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    const raw = await res.text();
    let parsed: Envelope | null = null;
    if (raw) {
      try {
        parsed = JSON.parse(raw) as Envelope;
      } catch {
        parsed = null;
      }
    }
    return { res, body: parsed };
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError("The request timed out. Please try again.", 0, "Timeout");
    }
    throw new ApiError(
      "We couldn't reach the server. Please check your connection and try again.",
      0,
      "NetworkError",
    );
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Performs a request and returns the unwrapped payload:
 * prefers the `{ code, data }` envelope's `data`, otherwise the raw body.
 * Throws {@link ApiError} on non-2xx status, a non-OK `code`, a timeout, or a
 * network failure.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { res, body } = await send(path, options);

  if (!res.ok) {
    notifyUnauthorized(res.status, options.token);
    throw new ApiError(messageFrom(body, `Request failed (${res.status})`), res.status, body?.code);
  }
  if (body && typeof body.code === "string" && body.code !== "OK") {
    throw new ApiError(messageFrom(body, "The server returned an error."), res.status, body.code);
  }
  if (body && body.data !== undefined) return body.data as T;
  return (body ?? {}) as T;
}

/**
 * Like {@link request} but returns the FULL JSON body. Used by auth endpoints,
 * which return `token` / `user` at the top level rather than under `data`.
 */
export async function requestBody<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { res, body } = await send(path, options);

  if (!res.ok) {
    notifyUnauthorized(res.status, options.token);
    throw new ApiError(messageFrom(body, `Request failed (${res.status})`), res.status, body?.code);
  }
  if (body && typeof body.code === "string" && body.code !== "OK") {
    throw new ApiError(messageFrom(body, "The server returned an error."), res.status, body.code);
  }
  return (body ?? {}) as T;
}

/**
 * Read helper for services: returns demo data when offline, otherwise performs
 * the real request and lets any {@link ApiError} bubble up to the caller.
 */
export function readThrough<T>(path: string, demo: T, options: RequestOptions = {}): Promise<T> {
  if (IS_OFFLINE) return Promise.resolve(demo);
  return request<T>(path, options);
}